import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { useMemo } from 'react'
import { User } from 'graphqlAPI'
import ProfilesCountCell from 'components/reusables/profilesCountCell'
import { getPercentage } from 'helpers'
import { useAccess } from 'contexts/AccessContext'
import { ProgressEvaluation } from '../../../../components/ProgressEvaluation'
import EvaluationProgressPopover from '../../../../components/EvaluationProgressPopover/index'
import tableStyles from '../../Participants.module.scss'
import Options from './Options'
import UserProgress from './UserProgress'
import styles from './useColumns.module.scss'
import { ParticipantsColumns, prefixTable } from './constants'
import { CoursesCount } from './CoursesCount'

const ACTION_COLUMN_WIDTH = 45
const NAME_COLUMN_WIDTH = 260
const PROFILES_COLUMN_WIDTH = 130

type Data = User

export function useColumns() {
  const { t } = useTranslation()
  const { can, access } = useAccess()
  const canViewParticipantProfile = access.canViewParticipantProfile()
  const canManageProgress = can('manageProgress')
  const showActionColumn = canViewParticipantProfile || canManageProgress
  type Column = {
    title: string
    key: ParticipantsColumns
    sorteable?: boolean
    width?: number
    hideable?: boolean
    align?: 'left' | 'center' | 'right'
    fixed?: 'left' | 'right'
    className?: string
    render?: (...data: any) => any
  }

  const prefixHeaders = `${prefixTable}.headers`
  const prefixColumns = `${prefixTable}.columns`

  const columns: Column[] = useMemo(() => {
    const cols: Column[] = [
      {
        title: t(`${prefixHeaders}.name`),
        key: ParticipantsColumns.name,
        sorteable: true,
        hideable: false,
        width: NAME_COLUMN_WIDTH,
        fixed: 'left',
        className: tableStyles.nameColumn,
        render: (_, { id, fullName }: Data) => {
          if (!canViewParticipantProfile) {
            return <span>{fullName}</span>
          }

          return (
            <Link
              to={`/configuration/user-profile/${id}`}
              className={styles.link}
            >
              {fullName}
            </Link>
          )
        },
      },
      {
        title: t(`${prefixHeaders}.area`),
        key: ParticipantsColumns.area,
        width: 170,
        sorteable: true,
        render: (_, { area }: Data) => <span>{area?.name || '---'}</span>,
      },
      {
        title: t(`${prefixHeaders}.enrolledCourses`, {
          defaultValue: 'Cursos inscritos',
        }),
        key: ParticipantsColumns.coursesCount,
        width: 70,
        sorteable: true,
        render: (_, value?: Data) => <CoursesCount value={value} />,
      },
      {
        title: t(`${prefixHeaders}.lms_profiles`),
        key: ParticipantsColumns.lmsProfiles,
        width: PROFILES_COLUMN_WIDTH,
        className: styles.profileColumn,
        sorteable: false,
        render: (_, { id, lmsProfiles }: Data) => (
          <ProfilesCountCell
            resourceId={id}
            resourceType="user"
            count={lmsProfiles?.length ?? 0}
          />
        ),
      },
      {
        title: t(`${prefixHeaders}.progress`),
        key: ParticipantsColumns.coursesAdvance,
        width: 60,
        sorteable: true,
        render: (_, value?: Data) =>
          (value?.allCoursesProgress && (
            <UserProgress
              id={'progress-' + value.id}
              progress={value.allCoursesProgress}
            />
          )) || <div className="text-center">---</div>,
      },
      {
        title: t(`${prefixHeaders}.progressEvaluations`),
        key: ParticipantsColumns.evaluationsApproved,
        sorteable: false,
        width: 120,
        render: (_, value: Data) => {
          const { allCoursesProgressEvaluations: progress } = value
          return (
            (progress && (
              <EvaluationProgressPopover
                progress={{
                  approved: progress.approved,
                  reproved: progress.reproved,
                }}
                total={progress.total}
              >
                <ProgressEvaluation
                  progress={progress}
                  total={progress.total}
                />
                {t(`${prefixColumns}.progressEvaluation`, {
                  count: getPercentage(progress.approved, progress.total),
                })}
              </EvaluationProgressPopover>
            )) ||
            '---'
          )
        },
      },
    ]

    if (showActionColumn) {
      cols.push({
        title: '',
        key: ParticipantsColumns.action,
        width: ACTION_COLUMN_WIDTH,
        hideable: false,
        align: 'center',
        fixed: 'right',
        className: tableStyles.actionColumn,
        render: (id, user: Data) => <Options user={user} />,
      })
    }

    return cols
  }, [
    canViewParticipantProfile,
    prefixColumns,
    prefixHeaders,
    showActionColumn,
    t,
  ])
  return columns
}
